// Custom hook for managing recipes with Firestore
import { useState, useEffect } from 'react';
import { getAllRecipes, getRecipeById, getDefaultRecipes, getUserRecipes } from '../services/recipeService';

export const useRecipes = (shouldFetch = true, useUserRecipes = false) => {
  const [recipes, setRecipes] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!shouldFetch) {
      setLoading(false);
      return;
    }

    const fetchRecipes = async () => {
      try {
        setLoading(true);
        let recipesData;
        
        if (useUserRecipes) {
          recipesData = await getUserRecipes();
        } else {
          recipesData = await getDefaultRecipes();
        }
        
        setRecipes(recipesData);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Failed to fetch recipes:', err);
        
        // Fallback to local JSON only for default recipes
        if (!useUserRecipes) {
          try {
            const localRecipes = await import('../data/recpies.json');
            setRecipes(localRecipes.default);
            console.log('Loaded recipes from local fallback');
          } catch (fallbackErr) {
            console.error('Fallback also failed:', fallbackErr);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRecipes();
  }, [shouldFetch, useUserRecipes]);

  const refreshRecipes = async () => {
    try {
      setLoading(true);
      const recipesData = await getAllRecipes();
      setRecipes(recipesData);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    recipes,
    loading,
    error,
    refreshRecipes
  };
};

export const useRecipe = (recipeId) => {
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!recipeId) {
      setRecipe(null);
      setLoading(false);
      return;
    }

    const fetchRecipe = async () => {
      try {
        setLoading(true);
        const recipeData = await getRecipeById(recipeId);
        setRecipe(recipeData);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Failed to fetch recipe:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecipe();
  }, [recipeId]);

  return {
    recipe,
    loading,
    error
  };
};